import { bemClassBuilder } from '@vista-digital/bem-class-builder'
import { deviceHasTouch } from '@vista-digital/occ-ui/src/lib/utils/device-has-touch'
import type { EmblaCarouselType } from 'embla-carousel'
import Autoplay from 'embla-carousel-autoplay'
import ClassNames from 'embla-carousel-class-names'
import useEmblaCarousel from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { observer } from 'mobx-react'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CarouselPositionIndicator, CarouselScrollButton } from './subcomponents'
import type { CarouselProps } from './types/props'

const b = bemClassBuilder('v-carousel')

/**
 * This component is a clone of the OCC Carousel arriving in v15.
 *
 * Once OCC v15 is released, and our projects are updated to use it, this component can be removed.
 *
 * Ensure to import the styles for this component:
 * `@use '@omnia/ui/src/lib/cms/components/carousel/scss';`
 */

export const Carousel: FC<CarouselProps> = observer(props => {
    const { children, config } = props
    const { settings, touchSettings, showPositionIndicators, disableScroll, scrollToSlide, autoPlay } = config

    const [scrollPreviousEnabled, setScrollPreviousEnabled] = useState(false)
    const [scrollNextEnabled, setScrollNextEnabled] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

    const carouselSettings = useMemo(() => {
        const hasTouch = deviceHasTouch()
        if (!hasTouch) {
            return settings
        }
        return { ...settings, ...touchSettings }
    }, [settings, touchSettings])

    const slidesToScroll = carouselSettings?.slidesToScroll ?? 'auto'

    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            ...carouselSettings,
            slidesToScroll,
            active: !disableScroll,
            align: 'start',
            containScroll: 'trimSnaps',
            watchSlides: api => {
                ;(() => {
                    api.scrollTo(0, true)
                })()

                return true
            },
        },
        [ClassNames({ snapped: '', inView: '' }), WheelGesturesPlugin(), ...(autoPlay ? [Autoplay(autoPlay)] : [])]
    )

    const scrollTo = useCallback(
        (index: number) => {
            // The scrollTo function takes the shortest path to the selected snap, when loop is `true` this is not visually desirable.
            // Providing the third undocumented parameter allows us specify  direction.
            // https://github.com/davidjerleke/embla-carousel/issues/434
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- copied from OCC v15
            // @ts-expect-error
            emblaApi?.scrollTo(index, false, index > selectedIndex ? -1 : 1)
        },
        [emblaApi, selectedIndex]
    )

    const onInit = useCallback(
        (api: EmblaCarouselType, targetSlideIndex?: number) => {
            setScrollSnaps(api.scrollSnapList())

            // Set slidesToScroll to 1 when displaying a single slide.
            if (slidesToScroll === 'auto' && api.slideNodes().length === 1) {
                api.reInit({ slidesToScroll: 1 })
            }

            if (targetSlideIndex) {
                const { slideRegistry } = api.internalEngine()
                const targetSnap = slideRegistry.findIndex(group => group.includes(targetSlideIndex))
                if (targetSnap >= 0) {
                    api.scrollTo(targetSnap)
                }
            }
        },
        [slidesToScroll]
    )

    const onSelect = useCallback((api: EmblaCarouselType) => {
        setSelectedIndex(api.selectedScrollSnap())
        setScrollPreviousEnabled(api.canScrollPrev())
        setScrollNextEnabled(api.canScrollNext())
    }, [])

    useEffect(() => {
        if (!emblaApi) {
            return
        }
        onInit(emblaApi, scrollToSlide)
        onSelect(emblaApi)

        emblaApi.on('resize', api => onInit(api, scrollToSlide))
        emblaApi.on('reInit', onSelect)
        emblaApi.on('select', onSelect)
    }, [emblaApi, onInit, onSelect, carouselSettings, scrollToSlide])

    const showScrollButtons = !disableScroll && !deviceHasTouch()

    return (
        <div className={b.block()}>
            {showScrollButtons && (
                <CarouselScrollButton
                    enabled={scrollPreviousEnabled}
                    onClick={() => {
                        emblaApi?.scrollPrev()
                    }}
                    direction="Previous"
                />
            )}
            <div className={b.element('viewport', { showScrollButtons })} ref={emblaRef}>
                <ul className={b.element('container')}>{children}</ul>
            </div>
            {showScrollButtons && (
                <CarouselScrollButton
                    enabled={scrollNextEnabled}
                    onClick={() => {
                        emblaApi?.scrollNext()
                    }}
                    direction="Next"
                />
            )}

            {showPositionIndicators !== false && (
                <div className={b.element('position-indicators-wrapper')} role="tabList">
                    {scrollSnaps.map((_, index) => (
                        <CarouselPositionIndicator
                            key={index}
                            index={index}
                            selected={index === selectedIndex}
                            onClick={() => scrollTo(index)}
                        />
                    ))}
                </div>
            )}
        </div>
    )
})
Carousel.displayName = 'Carousel'
